import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Box, Button, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, useDisclosure } from '@chakra-ui/react'
import type { OfferVehicle } from 'brokertslib/src/dtos/Offer'
import { Insurer, Risk } from 'BrokerTsLib/src/enums'
import { OfferStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import { useContext, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { useCarkulkaExtraOffersContext } from '@/context/CarkulkaExtraOffersProvider'
import { ProductSheetsVehicleInsurers } from '@/hooks/useProductSheets'
import { carkulkaOffersContext } from '../../context/CarkulkaOffersProvider'
import { offerStatusColors } from '../../data/offers/utils/offerStatusColors'
import { useSelectCalculation } from '../../hooks/useOffer'
import { useUpdateLastWizardStep } from '../../hooks/useWizard'
import { Check } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { AnimatedOfferWrapper, animationVariants } from './AnimatedOfferWrapper'
import { OfferHeader } from './OfferHeader'
import {
  BasicInfoTab,
  DiscountsTab,
  ExtraInsuranceTab,
  YearlyMilageTab,
} from './tabs/carkulka'
import { ProductSheetTab } from './tabs/carkulka/ProductSheetTab'

const OfferFooter = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box sx={{ bgColor: 'gray.100', borderBottomRadius: 'md' }} as="footer">
      {children}
    </Box>
  )
}

export const GetOfferDisplayId = (paramsId?: string, offerId?: number) => `${paramsId}/${offerId}`
export const GetOfferElementId = (offerId?: number) => `offer-${offerId}`

export const scrollToOfferWithOffset = (elementId: string) => {
  const element: Element | undefined = document.querySelector(`#${elementId}`) ?? undefined
  const insuranceInfo: Element | undefined = document.querySelector('.js-insurance-info-wrapper') ?? undefined
  const offset = insuranceInfo?.getBoundingClientRect()?.height
  const elementPosition = element?.getBoundingClientRect()?.top

  if (!offset || !elementPosition) return

  const offsetPosition = elementPosition + window.scrollY - offset - 24

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })
}

export const OfferCar = ({
  offer, isOrigClone = false
}: {
  offer: OfferVehicle
  isOrigClone?: boolean
}) => {
  const navigate = useNavigate()

  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams } = useContext(insuranceParamsContext)
  const { showAll } = useContext(carkulkaOffersContext)

  const { offers: addonOffers } = useCarkulkaExtraOffersContext()
  const activeGapOffer = addonOffers?.filter(x => x.riskId == Risk.GAP).find((x) => x.isActive)
  const isGapExtraOfferSelected = insuranceParams?.baseParams?.addonVariants?.find((x) => x.riskVariantId == activeGapOffer?.riskVariantId)?.selected ?? false

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const offerRef = useRef<HTMLDivElement>(null)
  const { mutateAsync: selectOffer } = useSelectCalculation()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const displayId = GetOfferDisplayId(paramsId, offer.offerId)
  const elementId = GetOfferElementId(offer.offerId)
  const { isOpen: isOpenIncorectGapDialog, onOpen: onOpenIncorectGapDialog, onClose: onCloseIncorectGapDialog } = useDisclosure()
  const isContract = !!insuranceParams?.generalParams.contractNumber

  const hasError = (offer.errors?.length ?? 0) > 0 ||
    offer.parts.some(p => (p.riskId === Risk.POV || p.riskId === Risk.HAV) && (p.notAvailable ?? false))

  const status = useMemo(() => {
    let status = OfferStatusEnum.Valid
    if (hasError) status = OfferStatusEnum.Error
    else if (insuranceParams?.generalParams.selectedOfferId === offer.offerId) status = OfferStatusEnum.Selected

    return status
  }, [hasError, insuranceParams?.generalParams.selectedOfferId])


  const handleToggle = (included: boolean) => {
    setIsVisible(showAll || included)
  }

  const [tabIndex, setTabIndex] = useState(0)
  const handleTabsChange = (index: number) => {
    setTabIndex(index)
    scrollToOfferWithOffset(elementId)
    // scrollToOffer(elementId)
  }

  const handleSelectOffer = () => {
    setIsSubmitting(true)

    // const extraOffers = addonOffers.reduce(
    //   (p: { [n: number]: OfferAddon[] }, c) => ({
    //     ...p,
    //     [c.riskVariantId]: [...(p[c.riskVariantId] || []), c],
    //   }),
    //   {},
    // )

    selectOffer({ paramsId: paramsId!, offerId: offer.offerId })
      .then(() => {
        if ((insuranceParams?.generalParams.lastWizardStep ?? 3) < 4) {
          updateStep({ paramsId: Number(paramsId), step: 4, stepsStep: 'carkulka' }).
            then(() => {
              refetchInsuranceParams().then(() => {
                navigate(`/carkulka/wizard/4/${paramsId}`)
              })
            })
        }
        else {
          refetchInsuranceParams().then(() => {
            navigate(`/carkulka/wizard/4/${paramsId}`)
          })
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  const incorectGapDialogRef = useRef<HTMLButtonElement>(null)
  const color = isOrigClone && status == OfferStatusEnum.Valid ? 'blue.100' : offerStatusColors[status]

  const showYearKmTab = [Insurer.Pillow, Insurer.Allianz].includes(offer.insurerId, 0)
  const showYearKmTabContent = showYearKmTab && tabIndex == 3
  const showProductSheetTab = ProductSheetsVehicleInsurers.includes(offer.insurerId, 0)
  const showProductSheetTabContent = showProductSheetTab && (showYearKmTab ? tabIndex == 4 : tabIndex == 3)

  return (
    //<OfferWrapper id={elementId}>
    <AnimatedOfferWrapper
      id={elementId}
      ref={offerRef}
      animate={isVisible ? 'visible' : 'hidden'}
      transition={{ duration: 0.3 }}
      variants={animationVariants}
    >
      <OfferHeader
        displayId={displayId}
        company={{ id: offer.insurerId, name: offer.insurerNameFull }}
        status={status}
        color={color}
        offer={offer}
        isDisabled={isContract}
        onToggle={handleToggle}
        isVisible={isVisible}
        isOrigClone={isOrigClone}
      />

      <OfferFooter>
        <Tabs variant="default" colorScheme="primary"
          // onChange={() => scrollToOffer()}          
          onChange={handleTabsChange}
          index={tabIndex}
        >
          <TabPanels>
            <TabPanel>
              {tabIndex == 0 && <BasicInfoTab color={color} offer={offer} />}
            </TabPanel>
            <TabPanel>
              {tabIndex == 1 && <ExtraInsuranceTab offer={offer} onSavedHandler={() => setTabIndex(0)} />}
            </TabPanel>
            <TabPanel>
              {tabIndex == 2 && <DiscountsTab offer={offer} onSavedHandler={() => setTabIndex(0)} />}
            </TabPanel>
            {showYearKmTab &&
              <TabPanel>
                {showYearKmTabContent && <YearlyMilageTab offer={offer} onSavedHandler={() => setTabIndex(0)} />}
              </TabPanel>
            }
            {showProductSheetTab &&
              <TabPanel>
                {showProductSheetTabContent && <ProductSheetTab offer={offer} />}
              </TabPanel>
            }
          </TabPanels>

          <Flex
            sx={{
              display: {
                base: 'grid',
                md: 'flex',
              },
              rowGap: 2,
              px: [3, 3, 4],
              pb: [3, 3, 4],
              alignItems: 'center',
              justifyContent: { base: 'stretch', md: 'space-between' },
            }}
          >
            <TabList>
              <Tab>Základní přehled</Tab>
              <Tab>Připojištění</Tab>
              <Tab>Slevy</Tab>
              {showYearKmTab &&
                <Tab>Nájezd kilometrů</Tab>
              }
              {showProductSheetTab &&
                <Tab>Dokumenty</Tab>
              }
            </TabList>

            <Flex
              sx={{
                gap: 8,
                alignItems: 'center',
                justifyContent: { base: 'space-between', lg: 'end' },
              }}
            >
              {status !== 'error' && !isContract && tabIndex == 0 &&
                <Button
                  colorScheme="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  loadingText="Ukládám..."
                  onClick={() => {
                    // v pripade sjenani POV bez HAV spolu s GAP MAX se ma vyhodit dialog, ze to nelze
                    if (isGapExtraOfferSelected
                      && activeGapOffer?.insurerId == Insurer.Fortegra
                      && activeGapOffer?.productVariant == 'MAX'
                      && !offer.parts.some(p => p.riskId == Risk.HAV)) {
                      onOpenIncorectGapDialog()
                    } else {
                      handleSelectOffer()
                    }
                  }}
                >
                  <Check />
                  Sjednat
                </Button>
              }
            </Flex>
          </Flex>
        </Tabs>
      </OfferFooter>

      <AlertDialog
        leastDestructiveRef={incorectGapDialogRef}
        isOpen={isOpenIncorectGapDialog}
        onClose={onCloseIncorectGapDialog}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Nelze sjednat
            </AlertDialogHeader>

            <AlertDialogBody>
              Nabídku nelze sjednat s vybranou variantou GAP připojištění ({activeGapOffer?.name}). <br /><br />
              Upravte prosím variantu GAP připojištění.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button colorScheme="primary" onClick={onCloseIncorectGapDialog}>
                Zavřít
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AnimatedOfferWrapper>
  )
}
